<template>
<nav>
    <!-- ===== App bar ===== -->
    <v-app-bar app flat class="app-bar" height="64">
      <v-app-bar-nav-icon class="nav-icon" @click="drawer = !drawer"></v-app-bar-nav-icon>

      <div class="app-brand d-none d-sm-flex">
        <img src="../assets/realmow.png" alt="Ministry Logo" class="app-logo" />
        <div class="app-titles">
          <span class="app-title">MOWFAHR Portal</span>
          <span class="app-subtitle">Ministry of Women, Family Affairs &amp; Human Rights</span>
        </div>
      </div>

      <v-spacer></v-spacer>

      <v-menu offset-y transition="slide-y-transition" :close-on-content-click="true">
        <template v-slot:activator="{ on, attrs }">
          <v-btn text class="user-btn" v-bind="attrs" v-on="on">
            <v-avatar size="34" class="user-avatar">
              <img v-if="userPic" :src="userPic" alt="avatar" />
              <span v-else>{{ initials }}</span>
            </v-avatar>
            <div class="user-meta d-none d-md-flex">
              <span class="user-name">{{ users }}</span>
              <span class="user-role">{{ userRole }}</span>
            </div>
            <v-icon small class="ml-1" color="#8a94a6">mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-card min-width="230" class="user-menu">
          <div class="user-menu-head">
            <v-avatar size="42" class="user-avatar lg">
              <img v-if="userPic" :src="userPic" alt="avatar" />
              <span v-else>{{ initials }}</span>
            </v-avatar>
            <div>
              <div class="um-name">{{ users }}</div>
              <div class="um-sub">{{ userRole }}<span v-if="userDistrict"> &middot; {{ userDistrict }}</span></div>
            </div>
          </div>
          <v-divider></v-divider>
          <v-list dense>
            <v-list-item @click="changePassword">
              <v-list-item-icon class="mr-3">
                <v-icon small color="#5b6b84">mdi-lock-reset</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="um-item">Change password</v-list-item-title>
            </v-list-item>
            <v-list-item @click="openPhotoDialog">
              <v-list-item-icon class="mr-3">
                <v-icon small color="#5b6b84">mdi-camera-account</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="um-item">Change photo</v-list-item-title>
            </v-list-item>
            <v-list-item @click="logout">
              <v-list-item-icon class="mr-3">
                <v-icon small color="#d32f2f">mdi-power</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="um-item" style="color:#d32f2f">Logout</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
    </v-app-bar>

    <!-- ===== Sidebar ===== -->
    <v-navigation-drawer v-model="drawer" app class="side-nav" width="264">
      <div class="side-head">
        <img src="../assets/realmow.png" alt="Ministry Logo" class="side-logo" />
        <div class="side-head-text">
          <span class="side-title">MOWFAHR</span>
          <span class="side-sub">Management Portal</span>
        </div>
      </div>

      <div class="nav-section">Main Menu</div>

      <v-list dense nav class="side-list">
        <template v-for="(item, i) in items">
          <v-list-item
            v-if="!item.subLinks && item.show"
            :key="i"
            :to="item.route"
            class="nav-item"
            active-class="nav-item-active"
          >
            <v-list-item-icon class="nav-icon-wrap">
              <v-icon class="nav-item-icon">{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="nav-item-title">{{ item.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-group
            v-else-if="item.show"
            :key="'g' + i"
            no-action
            class="nav-group"
          >
            <template v-slot:activator>
              <v-list-item-icon class="nav-icon-wrap">
                <v-icon class="nav-item-icon">{{ item.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="nav-item-title">{{ item.title }}</v-list-item-title>
              </v-list-item-content>
            </template>
            <v-list-item
              v-for="sublink in item.subLinks"
              :to="sublink.route"
              :key="sublink.title"
              class="nav-item nav-sub"
              active-class="nav-item-active"
            >
              <v-list-item-icon class="nav-icon-wrap">
                <v-icon class="nav-item-icon" small>{{ sublink.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="nav-item-title">{{ sublink.title }}</v-list-item-title>
            </v-list-item>
          </v-list-group>
        </template>
      </v-list>

      <template v-slot:append>
        <div class="side-foot">
          <v-icon small color="#7e9cc9">mdi-shield-check-outline</v-icon>
          <span>District-secured access</span>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- ===== Change Password Dialog ===== -->
    <v-dialog v-model="passwordDialog" max-width="420" persistent>
      <v-card class="pwd-card">
        <v-card-title class="pwd-head">
          <v-icon left color="#fff">mdi-lock-reset</v-icon>
          Change Password
        </v-card-title>
        <v-card-text class="pb-0">
          <v-alert v-if="passwordError" type="error" dense text class="mb-3">{{ passwordError }}</v-alert>
          <v-alert v-if="passwordSuccess" type="success" dense text class="mb-3">{{ passwordSuccess }}</v-alert>

          <v-text-field
            v-model="passwordForm.current"
            label="Current password"
            :type="passwordForm.showCurrent ? 'text' : 'password'"
            :append-icon="passwordForm.showCurrent ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append="passwordForm.showCurrent = !passwordForm.showCurrent"
            outlined
            dense
            hide-details="auto"
            class="mb-3"
            @keyup.enter="savePassword"
          />

          <v-text-field
            v-model="passwordForm.new"
            label="New password"
            :type="passwordForm.showNew ? 'text' : 'password'"
            :append-icon="passwordForm.showNew ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append="passwordForm.showNew = !passwordForm.showNew"
            outlined
            dense
            hide-details="auto"
            class="mb-3"
            @keyup.enter="savePassword"
          />

          <v-text-field
            v-model="passwordForm.confirm"
            label="Confirm new password"
            :type="passwordForm.showConfirm ? 'text' : 'password'"
            :append-icon="passwordForm.showConfirm ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append="passwordForm.showConfirm = !passwordForm.showConfirm"
            outlined
            dense
            hide-details="auto"
            @keyup.enter="savePassword"
          />
        </v-card-text>
        <v-card-actions class="pwd-actions">
          <v-spacer />
          <v-btn text :disabled="passwordLoading" @click="passwordDialog = false; resetPasswordForm()">
            Cancel
          </v-btn>
          <v-btn class="ph-btn" :loading="passwordLoading" @click="savePassword">
            Update Password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== Change Photo Dialog ===== -->
    <v-dialog v-model="photoDialog" max-width="380" persistent>
      <v-card class="pwd-card">
        <v-card-title class="pwd-head">
          <v-icon left color="#fff">mdi-camera-account</v-icon>
          Change Profile Photo
        </v-card-title>
        <v-card-text class="pt-5 pb-0 text-center">
          <v-alert v-if="photoError" type="error" dense text class="mb-3 text-left">{{ photoError }}</v-alert>

          <v-avatar size="96" class="photo-preview mb-4">
            <v-img v-if="photoPreview" :src="photoPreview" />
            <v-img v-else-if="userPic" :src="userPic" />
            <span v-else class="photo-initials">{{ initials }}</span>
          </v-avatar>

          <v-file-input
            v-model="photoFile"
            accept="image/*"
            label="Choose image"
            prepend-icon="mdi-camera"
            outlined
            dense
            show-size
            @change="onPhotoSelected"
          />
        </v-card-text>
        <v-card-actions class="pwd-actions">
          <v-spacer />
          <v-btn text :disabled="photoLoading" @click="photoDialog = false; resetPhotoForm()">
            Cancel
          </v-btn>
          <v-btn class="ph-btn" :loading="photoLoading" :disabled="!photoFile" @click="savePhoto">
            Save Photo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== Signing-out overlay ===== -->
    <v-overlay :value="loggingOut" :z-index="9999" :opacity="1" color="#0d2d63">
      <div class="logout-box">
        <img src="../assets/realmow.png" alt="Ministry Logo" class="logout-logo" />
        <v-progress-circular indeterminate size="34" width="3" color="#90caf9" class="my-4"></v-progress-circular>
        <div class="logout-text">Signing you out&hellip;</div>
        <div class="logout-sub">Please wait</div>
      </div>
    </v-overlay>
</nav>
</template>

<script>
import axios from 'axios';
const url = process.env.VUE_APP_API_URL;
const UPLOAD_URL = process.env.VUE_APP_UPLOAD_URL;
const IMAGE_BASE = process.env.VUE_APP_IMAGE_BASE;

export default {
data(){
    return{
      drawer:true,
       items: [
        {title: 'Dashboard', icon: 'mdi-view-dashboard', show:true, route:'/'},
        {title: 'Care Giver Registration', icon: 'mdi-account-heart', route:'/mother', show:true},
        {title: 'Child Registration', icon:'mdi-human-child', route:'/child', show:true},
        {title: 'Clan Elder Registration', icon: 'mdi-account-tie', route:'/nabadoon', show:true},
       {title: 'Licence', icon: 'mdi-file-chart', route:'/licence', show:true},
        {title: 'Assign Child', icon: 'mdi-account-arrow-right', route:'/assign', show:true},
        {title: 'Child Assessment', icon: 'mdi-clipboard-text', route:'/child-assessment', show:true},
        {title: 'Transfer Child', icon: 'mdi-account-switch', route:'/transfer', show:true},
        {title: 'Users', icon: 'mdi-account-multiple', route:'/users', show:true},
        {title: 'Districts', icon: 'mdi-map-marker-radius', route:'/districts', show:true},
        // {title: 'Report Page', icon: 'mdi-file-chart', route:'/report', show:true},
        {title: 'Report Page', icon: 'mdi-file-chart', route:'/assessment-report', show:true},
      ],
      users:'',
      userPic: null,
      userRole: '',
      userDistrict: '',
      loggingOut: false,
      bool:true,
      passwordDialog: false,
      passwordLoading: false,
      passwordError: '',
      passwordSuccess: '',
      passwordForm: {
        current: '',
        new: '',
        confirm: '',
        showCurrent: false,
        showNew: false,
        showConfirm: false,
      },
      photoDialog: false,
      photoFile: null,
      photoPreview: null,
      photoLoading: false,
      photoError: '',
    }
},
computed:{
  initials(){
    const n = (this.users || '').trim();
    if(!n) return '?';
    const parts = n.split(/\s+/);
    return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase();
  }
},
created(){
  this.userlogo();
  this.updateMenuByRole();
},
methods:{
  userlogo:function(){
    this.users=localStorage.getItem('fname');
    try{
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      this.userRole = u.role ? (u.role.charAt(0).toUpperCase() + u.role.slice(1)) : '';
      this.userDistrict = u.district || (Array.isArray(u.districts) && u.districts.length ? u.districts.join(', ') : '');
      if(u.pic) this.userPic = (process.env.VUE_APP_IMAGE_BASE || '') + u.pic;
    }catch(e){ /* ignore */ }
  },
  updateMenuByRole:function(){
    let role = null;
    try{
      // Prefer normalized user.role saved at login
      const uRaw = localStorage.getItem('user');
      if(uRaw){
        const u = JSON.parse(uRaw);
        const r = u && u.role;
        if(typeof r === 'string') role = r.trim().toLowerCase();
      }
    }catch(e){ /* ignore parse errors */ }

    // Fallback to older 'usertype' key if user object not present
    if(!role){
      const rt = localStorage.getItem('usertype');
      role = rt ? rt.toString().trim().toLowerCase() : null;
    }

    const isAdmin = role === 'admin';
    const isEditor = role === 'editor';

    // Admin: can see everything.
    // Editor: hide admin-only items (transfer, users, report).
    this.items = this.items.map(it => {
      if (!isAdmin && it.route === '/transfer') {
        return { ...it, show: false };
      }
      if (!isAdmin && it.route === '/users') {
        return { ...it, show: false };
      }
      if (!isAdmin && it.route === '/districts') {
        return { ...it, show: false };
      }
      if (!isAdmin && it.route === '/report') {
        return { ...it, show: false };
      }
      // All other items visible for both admin and editor
      return { ...it, show: true };
    });
  },
  changePassword(){
    this.passwordDialog = true;
    this.resetPasswordForm();
  },
  resetPasswordForm(){
    this.passwordForm = {
      current: '',
      new: '',
      confirm: '',
      showCurrent: false,
      showNew: false,
      showConfirm: false,
    };
    this.passwordError = '';
    this.passwordSuccess = '';
  },
  async savePassword(){
    this.passwordError = '';
    this.passwordSuccess = '';

    // Basic validation
    if(!this.passwordForm.current || !this.passwordForm.new || !this.passwordForm.confirm){
      this.passwordError = 'All fields are required.';
      return;
    }
    if(this.passwordForm.new.length < 4){
      this.passwordError = 'New password must be at least 4 characters.';
      return;
    }
    if(this.passwordForm.new !== this.passwordForm.confirm){
      this.passwordError = 'New password and confirmation do not match.';
      return;
    }

    this.passwordLoading = true;

    try{
      // Pull current session info
      const username = localStorage.getItem('username') || '';
      const uRaw = localStorage.getItem('user');
      const user = uRaw ? JSON.parse(uRaw) : {};
      const userId = user.id || user.UserID || null;

      if(!username || !userId){
        this.passwordError = 'Session expired. Please log in again.';
        this.passwordLoading = false;
        return;
      }

      // 1) Verify current password by calling the same login endpoint
      const verifyRes = await axios.post(url, {
        opcion: 23,
        op: 20,
        username: username,
        password: this.passwordForm.current,
      });

      const verifyRows = Array.isArray(verifyRes.data) ? verifyRes.data : [];
      const first = verifyRows.length ? verifyRows[0] : {};
      const msg = (first.msg || '').toString().toLowerCase();

      // The login endpoint returns msg: 'not' | 'fill' | 'wrong' on failure.
      // Any other value means the credentials matched.
      if(msg === 'not' || msg === 'fill' || msg === 'wrong'){
        this.passwordError = 'Current password is incorrect.';
        this.passwordLoading = false;
        return;
      }

      // 2) Update the password — send ALL existing fields so the backend
      // doesn't blank out FullName, Username, Role, etc.
      const updateRes = await axios.post(url, {
        opcion: 16,
        op: 3,
        UserID: userId,
        FullName: first.FullName || user.name || '',
        Username: first.Username || username,
        Pass: this.passwordForm.new,
        Role: first.Role || user.role || '',
        PicLoc: first.PicLoc || user.pic || '',
        status: first.status !== undefined ? first.status : 1,
        district: first.district || user.district || '',
      });

      console.log('Password update response:', updateRes.data);

      this.passwordSuccess = 'Password updated successfully.';
      this.passwordForm.current = '';
      this.passwordForm.new = '';
      this.passwordForm.confirm = '';

      // Auto-close after a short success pause
      setTimeout(() => {
        this.passwordDialog = false;
        this.resetPasswordForm();
      }, 1500);
    }catch(err){
      console.error('Change password failed', err);
      this.passwordError = 'Something went wrong. Please try again.';
    }finally{
      this.passwordLoading = false;
    }
  },
  openPhotoDialog(){
    this.photoDialog = true;
    this.resetPhotoForm();
  },
  resetPhotoForm(){
    this.photoFile = null;
    this.photoPreview = null;
    this.photoError = '';
  },
  onPhotoSelected(file){
    this.photoError = '';
    if(!file){ this.photoPreview = null; return; }
    if(!file.type || !file.type.startsWith('image/')){
      this.photoError = 'Please choose an image file.';
      this.photoFile = null;
      this.photoPreview = null;
      return;
    }
    this.photoPreview = URL.createObjectURL(file);
  },
  async savePhoto(){
    if(!this.photoFile) return;
    this.photoError = '';
    this.photoLoading = true;
    try{
      const uRaw = localStorage.getItem('user');
      const user = uRaw ? JSON.parse(uRaw) : {};
      const userId = user.id || user.UserID || null;
      if(!userId){
        this.photoError = 'Session expired. Please log in again.';
        this.photoLoading = false;
        return;
      }

      // 1) Upload the image — same contract as ChildVue (returns filename)
      const formData = new FormData();
      formData.append('file', this.photoFile);
      const upRes = await axios.post(UPLOAD_URL, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const filename = typeof upRes.data === 'string' ? upRes.data
        : (upRes.data && (upRes.data.filename || upRes.data.path || upRes.data.file)) || '';
      if(!filename){
        this.photoError = 'Upload failed. Please try again.';
        this.photoLoading = false;
        return;
      }

      // 2) Fetch the full user row so we don't blank other fields on update
      const listRes = await axios.post(url, { opcion: 16, op: 0 });
      const rows = Array.isArray(listRes.data) ? listRes.data : [];
      const me = rows.find(r => String(r.UserID) === String(userId)) || {};

      // 3) Update the record with the new PicLoc, preserving every other field
      await axios.post(url, {
        opcion: 16,
        op: 3,
        UserID: userId,
        FullName: me.FullName || user.name || localStorage.getItem('fname') || '',
        Username: me.Username || localStorage.getItem('username') || '',
        Pass: me.Pass || '',
        Role: me.Role || user.role || '',
        PicLoc: filename,
        status: me.status !== undefined ? me.status : 1,
        district: me.district || user.district || '',
      });

      // 4) Update local session + avatar immediately
      user.pic = filename;
      localStorage.setItem('user', JSON.stringify(user));
      this.userPic = IMAGE_BASE + filename;

      this.photoDialog = false;
      this.resetPhotoForm();
    }catch(err){
      console.error('Photo update failed', err);
      this.photoError = 'Something went wrong. Please try again.';
    }finally{
      this.photoLoading = false;
    }
  },
  logout(){
    // Show the signing-out overlay immediately so no page flash is visible
    this.loggingOut = true;

    // Clear all auth-related keys
    localStorage.removeItem('fname');
    localStorage.removeItem('username');
    localStorage.removeItem('usertype');
    localStorage.removeItem('user');
    localStorage.removeItem('status');

    // Point the URL at login FIRST, then reload — the app boots directly
    // on the login page with no NavBar and no flash of the old view.
    setTimeout(() => {
      window.location.hash = '#/auth/login';
      window.location.reload();
    }, 700);
  }
}
}
</script>

<style scoped>
/* ===== App bar ===== */
.app-bar {
   background: #ffffff !important;
   border-bottom: 1px solid #e6eaf2 !important;
}
.nav-icon { color: #45526b !important; }
.app-brand { align-items: center; gap: 10px; }
.app-logo { width: 38px; height: 38px; object-fit: contain; }
.app-titles { display: flex; flex-direction: column; line-height: 1.2; }
.app-title { font-size: .95rem; font-weight: 700; color: #16233f; }
.app-subtitle { font-size: .68rem; color: #8a94a6; letter-spacing: .2px; }

.user-btn {
   text-transform: none;
   padding: 4px 10px !important;
   border-radius: 10px;
   height: 48px !important;
}
.user-btn:hover { background: #f1f4fa !important; }
.user-avatar {
   background: linear-gradient(135deg, #1565c0, #0d47a1);
   color: #fff;
   font-size: .8rem;
   font-weight: 700;
}
.user-avatar.lg { font-size: 1rem; }
.user-meta { flex-direction: column; align-items: flex-start; margin-left: 10px; line-height: 1.2; }
.user-name { font-size: .82rem; font-weight: 600; color: #26314a; }
.user-role { font-size: .68rem; color: #8a94a6; }

.user-menu { border-radius: 12px !important; overflow: hidden; }
.user-menu-head {
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 14px 16px;
   background: #f7f9fd;
}
.um-name { font-size: .88rem; font-weight: 700; color: #26314a; }
.um-sub { font-size: .72rem; color: #8a94a6; }
.um-item { font-size: .84rem; }

/* ===== Sidebar ===== */
.side-nav {
   background: linear-gradient(180deg, #123a7d 0%, #0d2d63 55%, #0a2450 100%) !important;
}
.side-head {
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 18px 20px 16px;
   border-bottom: 1px solid rgba(255, 255, 255, .08);
}
.side-logo {
   width: 42px; height: 42px;
   object-fit: contain;
   background: rgba(255, 255, 255, .12);
   border-radius: 10px;
   padding: 4px;
}
.side-head-text { display: flex; flex-direction: column; line-height: 1.2; }
.side-title { color: #fff; font-weight: 800; font-size: 1rem; letter-spacing: .5px; }
.side-sub { color: #7e9cc9; font-size: .68rem; letter-spacing: .8px; text-transform: uppercase; }

.nav-section {
   padding: 18px 22px 6px;
   font-size: .66rem;
   font-weight: 700;
   letter-spacing: 1.2px;
   text-transform: uppercase;
   color: #5f7ca8;
}
.side-list { padding: 4px 12px; }
.nav-item {
   border-radius: 10px;
   margin-bottom: 2px;
   min-height: 42px;
}
.nav-item::before { border-radius: 10px; }
.nav-icon-wrap { margin-right: 12px !important; min-width: 24px; }
.nav-item-icon { color: #9db4d6 !important; font-size: 20px; }
.nav-item-title { color: #c8d6ec; font-size: .85rem; font-weight: 500; }
.nav-item:hover { background: rgba(255, 255, 255, .07); }
.nav-item:hover .nav-item-icon,
.nav-item:hover .nav-item-title { color: #fff !important; }
.nav-item-active {
   background: linear-gradient(90deg, rgba(66, 165, 245, .28), rgba(66, 165, 245, .10)) !important;
   box-shadow: inset 3px 0 0 #64b5f6;
}
.nav-item-active .nav-item-icon,
.nav-item-active .nav-item-title { color: #fff !important; font-weight: 600; }
.nav-sub { padding-left: 14px !important; }
.nav-group >>> .v-list-group__header { border-radius: 10px; }
.nav-group >>> .v-list-group__header .v-list-item__icon { color: #9db4d6 !important; }

.side-foot {
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 14px 20px;
   font-size: .7rem;
   color: #7e9cc9;
   border-top: 1px solid rgba(255, 255, 255, .08);
}

/* ===== Logout overlay ===== */
.logout-box {
   display: flex;
   flex-direction: column;
   align-items: center;
   text-align: center;
}
.logout-logo {
   width: 72px;
   height: 72px;
   object-fit: contain;
   background: rgba(255, 255, 255, .1);
   border-radius: 18px;
   padding: 8px;
}
.logout-text {
   color: #fff;
   font-size: 1rem;
   font-weight: 600;
   letter-spacing: .3px;
}
.logout-sub {
   color: #7e9cc9;
   font-size: .78rem;
   margin-top: 4px;
}

/* ===== Change Password dialog ===== */
.pwd-card { border-radius: 16px !important; overflow: hidden; }
.pwd-head {
   background: linear-gradient(135deg, #1565c0, #0d47a1) !important;
   color: #fff !important;
   font-size: 1rem !important;
   font-weight: 700 !important;
   padding: 16px 22px !important;
}
.pwd-actions { padding: 12px 22px 18px !important; }

/* ===== Change Photo dialog ===== */
.photo-preview {
   background: linear-gradient(135deg, #1565c0, #0d47a1);
   border: 3px solid #e3eaf6;
}
.photo-initials {
   color: #fff;
   font-size: 1.6rem;
   font-weight: 700;
}
</style>